import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { inject } from '@angular/core';
import { ErrorStore } from '@application/errors/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { pipe, switchMap, tap } from 'rxjs';
import { HotsiteService } from '../infra/hotsite.service';
import { THotsite, THotsiteConfiguration, THotsiteTerm } from '../entities/hotsite.model';
import { APP_CONFIG } from '@application/config';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { tapResponse } from '@ngrx/operators';


export interface IHotsiteStore {
    configuration: THotsiteConfiguration;
    link: string;
    offerPage: THotsite;
    contactPage: THotsite;
    registerPage: THotsite;
    term: THotsiteTerm;
    showConfig: boolean;
    showOfferForm: boolean;
    showContactForm: boolean;
    showRegisterForm: boolean;
    showTermForm: boolean;
}

export const hotsiteInitialStore: IHotsiteStore = {
    configuration: null,
    link: null,
    offerPage: null,
    contactPage: null,
    registerPage: null,
    term: null,
    showConfig: false,
    showOfferForm: false,
    showContactForm: false,
    showRegisterForm: false,
    showTermForm: false,
};

export const HotsiteStore = signalStore(
  { providedIn: 'root' },
  withState<IHotsiteStore>(hotsiteInitialStore),
  withMethods((store, errorStore = inject(ErrorStore), hotsiteService = inject(HotsiteService), appConfig = inject(APP_CONFIG), dxOdataStoreService = inject(DxOdataStoreService)) => {

    /**
     * Endereço público de uma página do hotsite.
     *
     * O domínio vem de `externalUrl.hotsite_domain`, e não de uma condição sobre
     * `environment`: `local` e `rc` consomem o hotsite de PRODUÇÃO
     * (`hotsiteservice-api.prd.g.actuar.cloud`), e a condição antiga — tudo o que
     * não fosse `prod` ia para homologação — devolvia um subdomínio de produção
     * montado em `stage.novo.actuar.com`. O endereço resolve, não dá erro, e a
     * página não existe.
     */
    const hotsiteLink = (subDomainParam: string, path?: string) => {
        const subDomain = subDomainParam ?? '*';
        const baseUrl = `https://${subDomain}.${appConfig.externalUrl.hotsite_domain}`;
        // O caminho vai em minúsculas: o hotsite só serve essa forma. Medido em
        // 14/08/2026 sobre uma página real (`Path` gravado como `allowFacial`):
        // `/allowfacial` responde 200 com a página, `/allowFacial` e
        // `/AllowFacial` respondem 404. O `Path` guarda a grafia que o
        // utilizador digitou, e é por isso que a ligação e o QR desta mesma tela
        // caem em 404 sempre que ele usa maiúsculas.
        return path ? `${baseUrl}/${path.toLowerCase()}` : baseUrl;
    }

    return {
      hotsiteLink,

      toggleConfigModal: (showConfig = !store.showConfig()) => {
        patchState(store, { showConfig });
      },

      toggleTermFormModal: (showTermForm = !store.showTermForm()) => {
        patchState(store, { showTermForm });
      },

      toggleOfferFormModal: (showOfferForm = !store.showOfferForm()) => {
        patchState(store, { showOfferForm });
      },

      toggleContactFormModal: (showContactForm = !store.showContactForm()) => {
        patchState(store, { showContactForm });
      },

      toggleRegisterFormModal: (showRegisterForm = !store.showRegisterForm()) => {
        patchState(store, { showRegisterForm });
      },

      getTermsDataSource: () => {
          return dxOdataStoreService.fromOdata({
              url: appConfig.apiUrl.odata_hotsite_service, metadata: 'Terms', key: 'TermId'
          })
      },

      getContactPagesDataSource: () => {
          return dxOdataStoreService.fromOdata({
              url: appConfig.apiUrl.odata_hotsite_service, metadata: 'HotSites', key: 'HotsiteId', params: {
                  $select: 'Name,Description,HotsiteId,HotsiteType,Path',
                  $filter: '(HotsiteType eq 0)'
              }
          })
      },
      getOfferPagesDataSource: () => {
          return dxOdataStoreService.fromOdata({
              url: appConfig.apiUrl.odata_hotsite_service, metadata: 'HotSites', key: 'HotsiteId', params: {
                  $select: 'Name,Description,HotsiteId,HotsiteType,Path',
                  $filter: '(HotsiteType eq 1)'
              }
          })
      },
      getRegisterPagesDataSource: () => {
          return dxOdataStoreService.fromOdata({
              url: appConfig.apiUrl.odata_hotsite_service, metadata: 'HotSites', key: 'HotsiteId', params: {
                  $select: 'Name,Description,HotsiteId,HotsiteType,Path',
                  $filter: '(HotsiteType eq 2)'
              }
          })
      },
      getHotsiteConfiguration: rxMethod<string>(pipe(
          tap(() => patchState(store, { ...setPropLoading('getHotsiteConfiguration') })),
          switchMap((payload) => hotsiteService.getHotsiteConfiguration(payload).pipe(
            tapResponse({
              next: (configuration) => {
                  const link = hotsiteLink(configuration?.Url);
                  patchState(store, { configuration, link, ...setPropLoaded('getHotsiteConfiguration')})
              },
              error: (error: HttpErrorResponse) => {
                console.error(error);
                patchState( store, { ...setPropError('getHotsiteConfiguration', error) });
                errorStore.setHttpError(error)
              }
            }),
          ))
      )),

      updateConfiguration: rxMethod<THotsiteConfiguration>(pipe(
          tap(() => patchState(store, { ...setPropLoading('updateConfiguration') })),
          switchMap((payload) => hotsiteService.updateHotsiteConfiguration(payload).pipe(
            tapResponse({
              next: (configuration) => {
                  const link = hotsiteLink(configuration?.Url);
                  patchState(store, { configuration, link, ...setPropLoaded('updateConfiguration')})
              },
              error: (error: HttpErrorResponse) => {
                console.error(error);
                patchState( store, { ...setPropError('updateConfiguration', error) });
                errorStore.setHttpError(error)
              }
            }),
          ))
      )),

      getOfferPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getOfferPage') })),
        switchMap((payload) => hotsiteService.getById(payload).pipe(
          tapResponse({
            next: (offerPage) => {
                patchState(store, { offerPage, ...setPropLoaded('getOfferPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('getOfferPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      getContactPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getContactPage') })),
        switchMap((payload) => hotsiteService.getById(payload).pipe(
          tapResponse({
            next: (contactPage) => {
                patchState(store, { contactPage, ...setPropLoaded('getContactPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('getContactPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      getTerm: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getTerm') })),
        switchMap((payload) => hotsiteService.getTerm(payload).pipe(
          tapResponse({
            next: (term) => {
                patchState(store, { term, ...setPropLoaded('getTerm')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('getTerm', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),
      

      getRegisterPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getRegisterPage') })),
        switchMap((payload) => hotsiteService.getById(payload).pipe(
          tapResponse({
            next: (registerPage) => {
                patchState(store, { registerPage, ...setPropLoaded('getRegisterPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('getRegisterPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      deleteTerm: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('deleteTerm') })),
        switchMap((payload) => hotsiteService.deleteTerm(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('deleteTerm')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('deleteTerm', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      deleteOfferPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('deleteOfferPage') })),
        switchMap((payload) => hotsiteService.deleteHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('deleteOfferPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('deleteOfferPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      deleteContactPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('deleteContactPage') })),
        switchMap((payload) => hotsiteService.deleteHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('deleteContactPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('deleteContactPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      deleteRegisterPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('deleteRegisterPage') })),
        switchMap((payload) => hotsiteService.deleteHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('deleteRegisterPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('deleteRegisterPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      updateContactPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateContactPage') })),
        switchMap((payload) => hotsiteService.updateHotsite(payload).pipe(
          tapResponse({
            next: (contactPage) => {
                patchState(store, { contactPage, ...setPropLoaded('updateContactPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('updateContactPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      updateTerm: rxMethod<THotsiteTerm>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateTerm') })),
        switchMap((payload) => hotsiteService.updateTerm(payload).pipe(
          tapResponse({
            next: (term) => {
                patchState(store, { term, ...setPropLoaded('updateTerm')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('updateTerm', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      createTerm: rxMethod<THotsiteTerm>(pipe(
        tap(() => patchState(store, { ...setPropLoading('createTerm') })),
        switchMap((payload) => hotsiteService.createTerm(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('createTerm')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('createTerm', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),
      

      createContactPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('createContactPage') })),
        switchMap((payload) => hotsiteService.createHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('createContactPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('createContactPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      updateRegisterPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateRegisterPage') })),
        switchMap((payload) => hotsiteService.updateHotsite(payload).pipe(
          tapResponse({
            next: (registerPage) => {
                patchState(store, { registerPage, ...setPropLoaded('updateRegisterPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('updateRegisterPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      createRegisterPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('createRegisterPage') })),
        switchMap((payload) => hotsiteService.createHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('createRegisterPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('createRegisterPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      updateOfferPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateOfferPage') })),
        switchMap((payload) => hotsiteService.updateHotsite(payload).pipe(
          tapResponse({
            next: (offerPage) => {
                patchState(store, { offerPage, ...setPropLoaded('updateOfferPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('updateOfferPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      createOfferPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('createOfferPage') })),
        switchMap((payload) => hotsiteService.createHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('createOfferPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('createOfferPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),


      setPropsInit(props: string[]): void {
        patchState(store, {...setPropInit(...props)});
      },

      blankPagesState(): void {
        patchState(store, { offerPage: null, contactPage: null, registerPage: null, term: null }, 
        setPropInit('getOfferPage', 'getContactPage', 'getRegisterPage', 'deleteOfferPage', 'deleteContactPage', 'deleteRegisterPage', 'updateContactPage', 'createContactPage', 'updateRegisterPage', 'createRegisterPage', 'createOfferPage', 'updateOfferPage', 'getTerm', 'deleteTerm', 'createTerm', 'updateTerm'));
      }
    }
  }),
  withReqState('getHotsiteConfiguration', 'updateConfiguration', 'getOfferPage', 'getContactPage', 'getRegisterPage', 'deleteOfferPage', 'deleteContactPage', 'deleteRegisterPage', 'updateContactPage', 'createContactPage', 'updateRegisterPage', 'createRegisterPage', 'createOfferPage', 'updateOfferPage', 'getTerm', 'deleteTerm', 'createTerm', 'updateTerm')
)


